import { useCallback, useEffect, useRef, useState } from 'react';
import { FormAData } from '../FormA';
import { IdentificationFormData } from '../identification';
import { FormKData } from '../types/formK.types';
import { CommunicationLanguage, KycFormData } from '../types/kycFormTabs.types';
import { getDefaultControllingOwnerValues } from '../utils/formK.utils';

interface UseKycFormDataOptions {
  isFormComplete: (formId: string, data: KycFormData) => boolean;
  setActiveTab: (tab: string) => void;
  getRequiredFormIds: (data: KycFormData) => string[];
  /** Initial form data for pre-filling (used in admin edit mode) */
  initialData?: KycFormData;
}

/**
 * Empty/default state for Form K data
 * Always includes at least one controlling owner as required by the form
 */
const EMPTY_FORM_K_DATA: FormKData = {
  originOfAssets: [],
  originOfAssetsOther: '',
  controllingOwnerType: '',
  controllingOwners: [getDefaultControllingOwnerValues()],
  shareholdersOtherCompanies: '',
  fiduciaryHolding: '',
};

/**
 * Empty/default state for Form A data
 */
const EMPTY_FORM_A_DATA: FormAData = {
  beneficialOwnerType: '',
  beneficialOwners: [],
  originOfAssets: [],
  originOfAssetsOther: '',
};

/**
 * Custom hook for managing KYC form data state and handlers.
 * Handles all form data changes, submissions, and real-time updates.
 * Automatically clears form data when forms become hidden due to condition changes.
 */
export function useKycFormData({
  isFormComplete,
  setActiveTab,
  getRequiredFormIds,
  initialData,
}: UseKycFormDataOptions) {
  const [formData, setFormData] = useState<KycFormData>(initialData || {});
  const [communicationLanguage, setCommunicationLanguage] = useState<
    CommunicationLanguage | ''
  >(initialData?.communicationLanguage || '');

  const [communicationLanguageError, setCommunicationLanguageError] =
    useState<string>('');

  // Track previous required form IDs to detect visibility changes
  const prevRequiredFormIdsRef = useRef<string[]>([]);

  // Debounced formData for tab visibility calculations
  // Reduces recalculations during rapid form changes (e.g., typing)
  const [debouncedFormData, setDebouncedFormData] = useState(formData);

  // Debounce formData updates to reduce expensive tab visibility checks
  useEffect(() => {
    // For tab-relevant fields, use shorter debounce (100ms)
    // For other fields, use longer debounce (500ms)
    const isTabRelevantChange =
      formData.identification?.country !==
        debouncedFormData.identification?.country ||
      formData.identification?.legalForm !==
        debouncedFormData.identification?.legalForm ||
      formData.identification?.isFinancialIntermediary !==
        debouncedFormData.identification?.isFinancialIntermediary ||
      formData.identification?.listedCompany !==
        debouncedFormData.identification?.listedCompany ||
      formData.identification?.subsidiaryOwnership !==
        debouncedFormData.identification?.subsidiaryOwnership ||
      formData.formK?.fiduciaryHolding !==
        debouncedFormData.formK?.fiduciaryHolding;

    const debounceDelay = isTabRelevantChange ? 100 : 500;

    const timeoutId = setTimeout(() => {
      setDebouncedFormData(formData);
    }, debounceDelay);

    return () => clearTimeout(timeoutId);
  }, [formData, debouncedFormData]);

  /**
   * Effect to clear form data when forms become hidden.
   * When a form transitions from visible to hidden (no longer in requiredFormIds),
   * its data is reset to empty values to ensure clean state.
   * Now uses debounced formData to reduce recalculation frequency.
   */
  useEffect(() => {
    const currentRequiredFormIds = getRequiredFormIds(debouncedFormData);
    const prevRequiredFormIds = prevRequiredFormIdsRef.current;

    // Skip on initial render (no previous state to compare)
    if (prevRequiredFormIds.length === 0) {
      prevRequiredFormIdsRef.current = currentRequiredFormIds;
      return;
    }

    // Check if Form K was visible but is now hidden
    const wasFormKVisible = prevRequiredFormIds.includes('form-k');
    const isFormKVisible = currentRequiredFormIds.includes('form-k');
    const formKBecameHidden = wasFormKVisible && !isFormKVisible;

    // Check if Form A was visible but is now hidden
    const wasFormAVisible = prevRequiredFormIds.includes('form-a');
    const isFormAVisible = currentRequiredFormIds.includes('form-a');
    const formABecameHidden = wasFormAVisible && !isFormAVisible;

    // Clear form data for forms that became hidden
    if (formKBecameHidden || formABecameHidden) {
      setFormData(prev => {
        const updated = { ...prev };

        if (formKBecameHidden) {
          updated.formK = EMPTY_FORM_K_DATA;
        }

        if (formABecameHidden) {
          updated.formA = EMPTY_FORM_A_DATA;
        }

        return updated;
      });

      // If active tab is a form that became hidden, switch to identification
      // This is handled by the parent component via requiredFormIds changes
    }

    // Update the ref with current state for next comparison
    prevRequiredFormIdsRef.current = currentRequiredFormIds;
  }, [debouncedFormData, getRequiredFormIds]);

  /**
   * Handle identification form submission and auto-advance to next form.
   */
  const handleIdentificationSubmit = useCallback(
    (data: IdentificationFormData) => {
      const updatedFormData = { ...formData, identification: data };
      setFormData(updatedFormData);

      // Calculate required forms based on updated data
      const requiredFormIds = getRequiredFormIds(updatedFormData);

      // Find the first incomplete required form after identification
      const nextForm = requiredFormIds.find(
        (formId: string) =>
          formId !== 'identification' &&
          !isFormComplete(formId, updatedFormData)
      );

      if (nextForm) {
        setActiveTab(nextForm);
      }
    },
    [formData, getRequiredFormIds, isFormComplete, setActiveTab]
  );

  /**
   * Handle identification form errors.
   */
  const handleIdentificationError = useCallback((error: Error) => {
    console.error('Identification form error:', error);
  }, []);

  /**
   * Handle real-time identification form data changes for persistence.
   */
  const handleIdentificationChange = useCallback(
    (data: Partial<IdentificationFormData>) => {
      setFormData(prev => ({
        ...prev,
        identification: {
          ...prev.identification,
          ...data,
        } as IdentificationFormData,
      }));
    },
    []
  );

  /**
   * Handle Form K submission and auto-advance to next form.
   */
  const handleFormKSubmit = useCallback(
    (data: FormKData) => {
      const updatedFormData = { ...formData, formK: data };
      setFormData(updatedFormData);

      // Calculate required forms based on updated data
      const requiredFormIds = getRequiredFormIds(updatedFormData);

      // Find the first incomplete required form after Form K
      const nextForm = requiredFormIds.find(
        (formId: string) =>
          formId !== 'form-k' && !isFormComplete(formId, updatedFormData)
      );

      if (nextForm) {
        setActiveTab(nextForm);
      }
    },
    [formData, getRequiredFormIds, isFormComplete, setActiveTab]
  );

  /**
   * Handle Form K errors.
   */
  const handleFormKError = useCallback((error: Error) => {
    console.error('Form K error:', error);
  }, []);

  /**
   * Handle real-time Form K data changes for persistence.
   */
  const handleFormKChange = useCallback((data: Partial<FormKData>) => {
    setFormData(prev => ({
      ...prev,
      formK: {
        ...prev.formK,
        ...data,
      } as FormKData,
    }));
  }, []);

  /**
   * Handle Form A submission (last form, no auto-advance).
   */
  const handleFormASubmit = useCallback(
    (data: FormAData) => {
      const updatedFormData = { ...formData, formA: data };
      setFormData(updatedFormData);
    },
    [formData]
  );

  /**
   * Handle Form A errors.
   */
  const handleFormAError = useCallback((error: Error) => {
    console.error('Form A error:', error);
  }, []);

  /**
   * Handle real-time Form A data changes for persistence.
   */
  const handleFormAChange = useCallback((data: Partial<FormAData>) => {
    setFormData(prev => ({
      ...prev,
      formA: {
        ...prev.formA,
        ...data,
      } as FormAData,
    }));
  }, []);

  /**
   * Handle communication language change.
   */
  const handleCommunicationLanguageChange = useCallback(
    (language: CommunicationLanguage) => {
      setCommunicationLanguage(language);
      // Clear error when a valid selection is made
      if (language) {
        setCommunicationLanguageError('');
      }
      setFormData(prev => ({
        ...prev,
        communicationLanguage: language,
      }));
    },
    []
  );

  return {
    formData,
    communicationLanguage,
    communicationLanguageError,
    setCommunicationLanguageError,
    handleIdentificationSubmit,
    handleIdentificationError,
    handleIdentificationChange,
    handleFormKSubmit,
    handleFormKError,
    handleFormKChange,
    handleFormASubmit,
    handleFormAError,
    handleFormAChange,
    handleCommunicationLanguageChange,
  };
}
